import { reindexHub } from '@/backend/hubs'
import type { Eventually } from '@/data/clientUtils'
import { useHubStore } from '@/data/Hubs/useHubStore'
import { useEntityStore } from '@/data/useEntityStore'
import { nonNullable } from '@/shared/utils/typeAssertions'
import { useMutation } from '@tanstack/vue-query'
import { toValue } from 'vue'

export function useHubReindexing(props: { workspaceId: Eventually<string> }) {
  const entityStore = useEntityStore()
  const hubStore = useHubStore()

  return useMutation({
    mutationFn: async (params: { hubId: string }) => {
      const workspaceId = nonNullable(toValue(props.workspaceId))

      const response = await reindexHub(workspaceId, params.hubId)
      if (!response.ok) {
        throw new Error('Failed to reindex hub', { cause: response.error })
      }
    },

    onMutate(params) {
      const hub = hubStore.hubs.get(params.hubId)
      if (!hub || !hub.index) {
        return
      }

      const entity = entityStore.getFullEntity(hub.index.entityId)
      if (!entity) return

      const indexField = entity.fields.get(hub.index.propertyId)
      if (!indexField) return
      const originalStatus = indexField.status
      indexField.status = 'computing'

      const rollback = () => {
        indexField.status = originalStatus
      }

      return { rollback }
    },
    onError(error, params, context) {
      context?.rollback?.()
    },

    onSuccess() {
      // Do not update the hub or indexing field here with the call response.
      // It will return data that is technically not stale at that moment, but will be in a second.
      // When you start indexing, `index` field should be in "computing" state,
      // but it will not be at the time of this mutation. It will be "complete" + "stale",
      // making us show the "Reindex" button again in the UI.
      // Instead, we just save optimistic data as "computing" and wait for a WS event to come in a moment later.
    },
  })
}
